import { useCallback, useMemo, useState } from 'react';
import { tv } from 'tailwind-variants';

import { AgentOptionItem } from '@Models';

/** Separator between a row's label and its answer in the submitted message. */
export const CHOICE_SEPARATOR = ' → ';

const gridVariants = tv({
  slots: {
    root: 'mt-3 mb-1 flex flex-col gap-1',
    row: 'flex flex-col gap-2 rounded-lg border border-gray-100 bg-[#fbfaf9] px-3 py-2.5 animate-journey-slide-up sm:flex-row sm:items-center sm:justify-between',
    content: 'min-w-0 flex-1',
    label: 'text-sm font-semibold leading-snug text-gray-900',
    hint: 'mt-0.5 text-xs leading-snug text-gray-500',
    answers: 'flex flex-shrink-0 flex-wrap gap-1.5',
    answer:
      'rounded-md border px-3 py-1.5 text-xs font-semibold transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7c6cf6]/40 focus-visible:ring-offset-1 active:scale-[.98]',
    confirm:
      'mt-1 self-end rounded-lg bg-[#7c6cf6] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#6b5be0] disabled:cursor-not-allowed disabled:opacity-40',
    pending: 'mt-1 self-end text-xs leading-snug text-gray-500',
  },
  variants: {
    state: {
      idle: {
        answer: 'border-gray-200 bg-white text-gray-700 cursor-pointer hover:border-[#bdacfa] hover:bg-[#f5f3ff]',
      },
      picked: {
        answer: 'border-[#7c6cf6] bg-[#7c6cf6] text-white cursor-pointer',
      },
      disabled: {
        answer: 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed',
      },
    },
  },
  defaultVariants: { state: 'idle' },
});

interface ChoiceGridProps {
  rows: AgentOptionItem[];
  /** Persisted answers (rowId -> choiceId). When present the grid is locked. */
  answers?: Record<string, string>;
  disabled?: boolean;
  onConfirm?: (answered: { row: AgentOptionItem; choice: { id: string; label: string } }[]) => void;
}

/**
 * One question asked of several subjects at once — "did Target act on this?"
 * over every escalation awaiting an answer — with the same answer set on each
 * row.
 *
 * Rows left unanswered submit nothing. That is deliberate: these answers land
 * in an append-only audit table, and a row the user simply did not get to must
 * stay unanswered rather than be recorded as some default. It is the reason
 * this is a grid and not two chained multi-selects, where absence would have
 * silently meant an answer.
 *
 * On confirm the parent submits one `label → answer` line per answered row as
 * a single follow-up message, so the answers round-trip through message text
 * and survive a refresh (mirrors the single- and multi-select flows).
 */
const ChoiceGrid = ({ rows, answers, disabled = false, onConfirm }: ChoiceGridProps) => {
  const styles = gridVariants();
  const alreadyAnswered = Object.keys(answers ?? {}).length > 0;
  const [picked, setPicked] = useState<Record<string, string>>(() => answers ?? {});
  // Self-lock on confirm: closes the window before this turn stops being the
  // latest, which is what otherwise locks the block via `disabled`.
  const [submitted, setSubmitted] = useState(false);
  const locked = disabled || alreadyAnswered || submitted;

  const pick = useCallback(
    (rowId: string, choiceId: string) => {
      if (locked) return;
      setPicked((prev) => ({ ...prev, [rowId]: choiceId }));
    },
    [locked],
  );

  const answered = useMemo(
    () =>
      rows
        .map((row) => {
          const choice = row.choices?.find((c) => c.id === picked[row.id]);
          return choice ? { row, choice } : null;
        })
        .filter((entry): entry is { row: AgentOptionItem; choice: { id: string; label: string } } => entry !== null),
    [rows, picked],
  );

  const unanswered = rows.length - answered.length;

  if (!rows || rows.length === 0) return null;

  return (
    <div className={styles.root()} role="group" aria-label="Choice grid">
      {rows.map((row, idx) => (
        <div key={row.id} className={styles.row()} style={{ animationDelay: `${idx * 0.08 + 0.05}s` }}>
          <div className={styles.content()}>
            <div className={styles.label()}>{row.label}</div>
            {row.hint && <div className={styles.hint()}>{row.hint}</div>}
          </div>
          <div className={styles.answers()} role="radiogroup" aria-label={row.label}>
            {(row.choices ?? []).map((choice) => {
              const isPicked = picked[row.id] === choice.id;
              let state: 'idle' | 'picked' | 'disabled' = 'idle';
              if (isPicked) state = 'picked';
              else if (locked) state = 'disabled';
              return (
                <button
                  key={choice.id}
                  type="button"
                  role="radio"
                  aria-checked={isPicked}
                  disabled={locked}
                  onClick={() => pick(row.id, choice.id)}
                  className={styles.answer({ state })}>
                  {choice.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {!locked && unanswered > 0 && answered.length > 0 && (
        <p className={styles.pending()}>
          {unanswered === 1 ? '1 row left unanswered — it will' : `${unanswered} rows left unanswered — they will`} be
          asked again next week.
        </p>
      )}
      {!locked && (
        <button
          type="button"
          className={styles.confirm()}
          disabled={answered.length === 0}
          onClick={() => {
            setSubmitted(true);
            onConfirm?.(answered);
          }}>
          {answered.length > 0 ? `Confirm (${answered.length} of ${rows.length})` : 'Confirm'}
        </button>
      )}
    </div>
  );
};

export default ChoiceGrid;
